import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { ProductsApiAdapter } from '../adapters/ProductsApiAdapter';
import { Filters } from '../models/Filters';
// import type { UseProductsFilters } from './useProducts';
import { Service } from '../services/Service';

export interface AdvancedFilter {
  column: string;
  operator: string;
  value: string;
}

export interface UseProductsFilters extends Filters {
  advanced_filters?: {
    filters: AdvancedFilter[];
    logic: 'AND' | 'OR';
  };
}

export const useProducts = (filters: UseProductsFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ProductsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['products', filters],
    queryFn: ({ pageParam = 1 }) => {
      return service.getPaginated({
        ...filters,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage) => lastPage?.nextPage ?? undefined,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    placeholderData: (previousData) => previousData,
  });

  const products = data?.pages.flatMap((page) => page?.products ?? []) ?? [];

  // Obtener el total del API (siempre sera el mismo en todas las paginas)
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    products,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    // Nuevas props para scroll infinito
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    // Stats útiles
    totalPages: data?.pages.length ?? 0,
    totalLocations: products.length,
    total: totalFromApi, // Total real del API (con filtros)
  };
};

export const useExportProductsExcel = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ProductsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  return useMutation({
    mutationKey: ['products-export', filters],
    mutationFn: (vars: Filters) => service.exportProductsExcel(vars),
  });
};

export const useDeleteProduct = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ProductsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  return useMutation({
    mutationKey: ['product-delete'],
    mutationFn: (productId: number) => service.deleteProduct(productId),
  });
};
